import React from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'

interface KeyboardShortcut {
  category: string
  shortcuts: {
    keys: string
    description: string
  }[]
}

const shortcuts: KeyboardShortcut[] = [
  {
    category: 'Common',
    shortcuts: [
      { keys: 'Cmd + F', description: 'Find (and replace)' },
      { keys: 'Cmd + Z', description: 'Undo' },
      { keys: 'Cmd + Y', description: 'Redo' },
      { keys: 'Cmd + Enter', description: 'Compile' },
    ],
  },
  {
    category: 'Navigation',
    shortcuts: [
      { keys: 'Cmd + Home', description: 'Beginning of document' },
      { keys: 'Cmd + End', description: 'End of document' },
      { keys: 'Cmd + Shift + L', description: 'Go To Line' },
    ],
  },
  {
    category: 'Editing',
    shortcuts: [
      { keys: 'Cmd + /', description: 'Toggle Comment' },
      { keys: 'Ctrl + U', description: 'To Uppercase' },
      { keys: 'Cmd + B', description: 'Bold text' },
      { keys: 'Cmd + D', description: 'Delete Current Line' },
      { keys: 'Ctrl + Shift + U', description: 'To Lowercase' },
      { keys: 'Cmd + I', description: 'Italic Text' },
      { keys: 'Cmd + A', description: 'Select All' },
      { keys: 'Tab', description: 'Indent / Accept Autocomplete / Next Tab Stop' },
    ],
  },
  {
    category: 'Autocomplete',
    shortcuts: [
      { keys: 'Ctrl + Space', description: 'Autocomplete Menu' },
      { keys: 'Up / Down', description: 'Select Candidate' },
      { keys: 'Enter / Tab', description: 'Insert Candidate' },
    ],
  },
  {
    category: 'Snippet Navigation',
    shortcuts: [
      { keys: 'Tab', description: 'Next Tab Stop (in snippets)' },
      { keys: 'Shift + Tab', description: 'Previous Tab Stop' },
      { keys: 'Escape', description: 'Exit Snippet Mode' },
    ],
  },
  {
    category: 'Review',
    shortcuts: [
      { keys: 'Cmd + J', description: 'Toggle review panel' },
      { keys: 'Cmd + Shift + A', description: 'Toggle track changes' },
      { keys: 'Cmd + Shift + C', description: 'Add a comment' },
    ],
  },
]

interface KeyboardShortcutsModalProps {
  isOpen: boolean
  onClose: () => void
}

const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Hotkeys</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {shortcuts.map((section) => (
              <div key={section.category}>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                  {section.category}
                </h3>
                <div className="space-y-2">
                  {section.shortcuts.map((shortcut, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
                    >
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {shortcut.description}
                      </span>
                      <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">
                        {shortcut.keys}
                      </kbd>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
            Press <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">Ctrl + Space</kbd> to manually trigger autocomplete at any time.
          </div>
        </div>
      </div>
    </div>
  )
}

export default KeyboardShortcutsModal

